/**
 * @file FooterLinksOrder.tsx
 * @description Список «Ссылки» нижней полосы подвала (phase19.16, приёмка
 * Р2а; SB118, SB115; uiux-footer-columns §9): ссылки платформы строками с
 * ⋮⋮ (перетаскивание, @dnd-kit) и «Выше» / «Ниже».
 *
 * Дополнение SB118 (решение 2026-10-11): «Условия возврата», «Условия
 * доставки и оплаты» и «Личный кабинет» владелец выключает переключателем
 * (`footer.legal.hiddenLinks`). У оферты и политики ПДн вместо
 * переключателя замок и «По закону на каждой странице». Пометка «сейчас
 * скрыта — есть в меню подвала» (SB115) — отдельно от выключения.
 *
 * Партия «Юридические документы» (SB142; uiux-legal-docs §5; проект данных
 * §6.1): свои строки (документ, группа, «Все документы») стоят вперемешку
 * с платформенными — без замка, с переключателем и ×. Общий порядок —
 * `mergeFooterRows`; после перетаскивания ключи раскладываются обратно
 * (`footerEntriesFromKeys` → `splitFooterRows`): порядок платформы в
 * `footer.legal.linksOrder`, якоря своих строк — в `footerDocs.items`.
 */

import { closestCenter, DndContext, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  FOOTER_HIDEABLE_LINKS,
  FOOTER_LOCKED_LINK_NOTE,
  FOOTER_PLATFORM_LINK_LABELS,
  footerDocItemKey,
  footerEntriesFromKeys,
  footerLinkKey,
  mergeFooterRows,
  splitFooterRows,
  type FooterDocItem,
  type FooterHideableLink,
  type FooterPlatformLink,
} from "@chaiberi/shared";
import { Lock, X } from "lucide-react";

import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { FooterSortableRow, WhereNote } from "./FooterSortableRow";
import type { FooterDocRowView, FooterLinkWhere } from "../../../lib/site-view/footer-link-where";
import { cn } from "@/lib/utils";

/** Ссылка, которую можно выключить, или null — под замком (оферта, политика ПДн). */
const hideableOf = (link: FooterPlatformLink): FooterHideableLink | null =>
  FOOTER_HIDEABLE_LINKS.find((h) => h === link) ?? null;

interface Props {
  linksOrder: readonly FooterPlatformLink[];
  /** Свои строки (`footerDocs.items`). */
  items: readonly FooterDocItem[];
  /** Ссылки, которые сейчас скрыты из-за дубля в меню подвала (SB115). */
  duplicated: ReadonlySet<FooterPlatformLink>;
  /** Выключенные владельцем (`footer.legal.hiddenLinks`). */
  switchedOff: ReadonlySet<FooterPlatformLink>;
  /** Новый общий порядок: порядок платформы и строки с пересчитанными якорями. */
  onChange: (next: { linksOrder: FooterPlatformLink[]; items: FooterDocItem[] }) => void;
  onToggle: (link: FooterHideableLink, show: boolean) => void;
  /** Своя строка: включить / выключить и убрать (по ключу `doc:<id>` / `all-documents`). */
  onToggleDoc: (key: string, on: boolean) => void;
  onRemoveDoc: (key: string) => void;
  /** Вид своей строки: название, вид, пометка состояния. */
  docView: (item: FooterDocItem) => FooterDocRowView;
  /** «Где задаётся» (SB121); нет — без подписей. */
  where?: Readonly<Record<FooterPlatformLink, FooterLinkWhere>>;
}

export function FooterLinksOrder({ linksOrder, items, duplicated, switchedOff, onChange, onToggle, onToggleDoc, onRemoveDoc, docView, where }: Props) {
  const entries = mergeFooterRows(linksOrder, items);
  const keys = entries.map(footerLinkKey);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const move = (from: number, to: number) => {
    if (to < 0 || to >= keys.length || from === to || from < 0) return;
    onChange(splitFooterRows(footerEntriesFromKeys(arrayMove(keys, from, to), items)));
  };
  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    move(keys.indexOf(String(active.id)), keys.indexOf(String(over.id)));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={keys} strategy={verticalListSortingStrategy}>
        <ol aria-label="Порядок ссылок нижней полосы" className="flex flex-col divide-y divide-[var(--border)] overflow-hidden rounded-xl bg-surface-lowest [box-shadow:inset_0_0_0_1px_var(--border)]">
          {entries.map((entry, index) => {
            const row = { index, count: keys.length, onMove: move };
            if ("platform" in entry) {
              const link = entry.platform;
              return (
                <PlatformRow key={link} link={link} {...row} duplicated={duplicated.has(link)} off={switchedOff.has(link)} onToggle={onToggle} where={where?.[link] ?? null} />
              );
            }
            const key = footerDocItemKey(entry.item);
            return <DocRow key={key} id={key} view={docView(entry.item)} {...row} onToggle={(on) => onToggleDoc(key, on)} onRemove={() => onRemoveDoc(key)} />;
          })}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

interface RowBase {
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
}

function PlatformRow({ link, duplicated, off, onToggle, where, ...row }: RowBase & { link: FooterPlatformLink; duplicated: boolean; off: boolean; onToggle: Props["onToggle"]; where: FooterLinkWhere | null }) {
  const label = FOOTER_PLATFORM_LINK_LABELS[link];
  const hideable = hideableOf(link);
  const controls =
    hideable === null ? (
      <span className="inline-flex size-12 shrink-0 items-center justify-center text-on-surface-variant">
        <Lock aria-label="Всегда на сайте" className="size-4" />
      </span>
    ) : (
      <label className="inline-flex size-12 shrink-0 cursor-pointer items-center justify-center">
        <Switch aria-label={`Показывать «${label}»`} checked={!off} onCheckedChange={(on) => onToggle(hideable, on)} />
      </label>
    );
  return (
    <FooterSortableRow id={link} label={label} {...row} controls={controls}>
      <span className={cn("type-body break-words", off ? "text-on-surface-variant" : "text-on-surface")}>{label}</span>
      {hideable === null ? <span className="type-caption text-on-surface-variant">{FOOTER_LOCKED_LINK_NOTE}</span> : null}
      {off ? <span className="type-caption text-on-surface-variant">выключена</span> : null}
      {duplicated && !off ? <span className="type-caption text-on-surface-variant">сейчас скрыта — есть в меню подвала</span> : null}
      {where ? <WhereNote where={where} /> : null}
    </FooterSortableRow>
  );
}

function DocRow({ id, view, onToggle, onRemove, ...row }: RowBase & { id: string; view: FooterDocRowView; onToggle: (on: boolean) => void; onRemove: () => void }) {
  const off = view.status === "off";
  const problem = view.status === "deleted";
  const controls = (
    <>
      {problem ? null : (
        <label className="inline-flex size-12 shrink-0 cursor-pointer items-center justify-center">
          <Switch aria-label={`Показывать «${view.label}»`} checked={!off} onCheckedChange={(on) => onToggle(on)} />
        </label>
      )}
      <Button variant="ghost" size="icon" className="size-12" aria-label={`Убрать «${view.label}» из «Ссылок»`} onClick={onRemove}>
        <X aria-hidden className="size-4" />
      </Button>
    </>
  );
  return (
    <FooterSortableRow id={id} label={view.label} {...row} controls={controls}>
      <span className={cn("type-body break-words", off || problem ? "text-on-surface-variant" : "text-on-surface")}>
        {view.label}
        {view.kind ? <span className="text-on-surface-variant"> · {view.kind}</span> : null}
      </span>
      {view.note ? <span className={cn("type-caption", problem ? "text-destructive" : "text-on-surface-variant")}>{view.note}</span> : null}
      {view.where ? <WhereNote where={view.where} /> : null}
    </FooterSortableRow>
  );
}
